import React, { useCallback, useEffect, useState } from 'react';
import { IconButton } from '@mui/material';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import Header from '../../components/shared/header';
import PageWrapperV2 from '../../components/shared/page-wrapper-v2';
import PageHeader from '../../components/shared/page-header';
import PageBody from '../../components/shared/page-body';
import PageFooter from '../../components/shared/page-footer';
import PageTableContainer from '../../components/shared/page-table-container';
import TiminatorTableV2 from '../../components/shared/table-v2';
import IconTextLarge from '../../components/shared/large-icon-text';
import { ClientFilter, DEFAULT_CLIENT_FILTER_STATE } from './components/client-filter';
import { SVGS } from '../../assets/images';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { deleteClient, getClients } from '../../features/clients';
import { Client } from '../../features/clients/types';
import { useInfinitePagination } from '../../hooks/useInfinitePagination';
import { useClientsTable } from './hook/useClientsTable';
import { Lx } from '../../i18n/consts';
import CreateNewButtonV2 from '../../components/shared/create-new-button-v2';
import { NAVIGATION_ROUTES } from '../../routes/NavigationRoute';
import { DeleteDialog } from '../../components/shared/delete-dialog';
import { canManageClients } from '../../features/profile/entityPermissions';

import styles from './clients.module.scss';

export default function ClientPage() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const intl = useIntl();
    const isMobile = useMediaQuery({ maxWidth: 1000 });

    const clientsState = useAppSelector(store => store.clients);
    const profile = useAppSelector(store => store.profile);
    const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedClient, setSelectedClient] = useState<Client | null>(null);
    const canEditClients = canManageClients(profile);

    const hasMore = clientsState.items.length < clientsState.total;

    const fetchClientsPage = useCallback(
        (page: number) => dispatch(getClients(page)).unwrap(),
        [dispatch],
    );

    const {
        loadInitial,
        loadNext,
        isLoadingMore,
        isLoadingInitial,
        reset,
    } = useInfinitePagination({
        hasMore,
        fetchPage: fetchClientsPage,
    });

    const [search, setSearch] = useState('');
    const [selectedStatus, setSelectedStatus] = useState<number>(-1);
    const [mobileView, setMobileView] = useState<'list' | 'filter'>('list');

    useEffect(() => {
        loadInitial().catch(() => {
            /* handled elsewhere */
        });
    }, [loadInitial]);

    useEffect(() => {
        if (!isMobile) {
            setMobileView('list');
        }
    }, [isMobile]);

    const handleDeleteClick = useCallback((client: Client) => {
        setSelectedClient(client);
        setDeleteDialogOpen(true);
    }, []);

    const tableSettings = useClientsTable({
        search,
        selectedStatus,
        isMobile,
        canDelete: canEditClients,
        onDeleteClick: handleDeleteClick,
    });

    const clientCount = tableSettings.data?.length ?? 0;
    const isEmpty = !isLoadingInitial && tableSettings.isEmpty;
    const showMobileFilterToggle = isMobile && !isEmpty;
    const shouldShowCreateFooterButton = isMobile && mobileView === 'list' && canEditClients;
    const isMobileFilterActive = isMobile && mobileView === 'filter';

    const handleLoadMore = useCallback(() => {
        loadNext().catch(() => {
            /* handled elsewhere */
        });
    }, [loadNext]);

    const handleRowClick = useCallback(
        (client: Client) => {
            if (!canEditClients) {
                return;
            }

            navigate(NAVIGATION_ROUTES.EDIT_CLIENT, {
                state: { client },
            });
        },
        [canEditClients, navigate],
    );

    const handleCreateClick = useCallback(() => {
        if (!canEditClients) {
            return;
        }

        navigate(NAVIGATION_ROUTES.CREATE_CLIENT);
    }, [canEditClients, navigate]);

    const handleMobileFilterClick = useCallback(() => {
        setMobileView(prev => (prev === 'list' ? 'filter' : 'list'));
    }, []);

    const handleMobileFilterClose = useCallback(() => {
        setMobileView('list');
    }, []);

    const handleFilterChange = useCallback(({ search: nextSearch, selectedStatus: nextStatus }: { search: string; selectedStatus: number }) => {
        setSearch(nextSearch);
        setSelectedStatus(nextStatus);
    }, []);

    const reloadClients = useCallback(async () => {
        reset();
        setMobileView('list');
        try {
            await loadInitial();
        } catch {
            // handled elsewhere
        }
    }, [loadInitial, reset]);

    const handleDeleteCancel = useCallback(() => {
        setDeleteDialogOpen(false);
        setSelectedClient(null);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        if (!selectedClient) {
            return;
        }

        try {
            await dispatch(deleteClient(selectedClient.id)).unwrap();
            await reloadClients();
        } finally {
            handleDeleteCancel();
        }
    }, [dispatch, reloadClients, selectedClient, handleDeleteCancel]);

    return (
        <>
            <PageWrapperV2>
                <PageHeader>
                    <div className={styles.header}>
                        <div className={styles.topRow}>
                            <div className={styles.title}>
                                <Header headerLocalizationId={Lx.General.CLIENTS} loading={isLoadingInitial}>
                                    <div className={styles.count}>{clientCount}</div>
                                </Header>
                            </div>
                            <div className={styles.actions}>
                                {isMobile ? (
                                    showMobileFilterToggle &&
                                        <IconButton
                                            aria-label='filterIconButton'
                                            onClick={() => {
                                                if (isMobileFilterActive) {
                                                    handleFilterChange(DEFAULT_CLIENT_FILTER_STATE);
                                                }
                                                handleMobileFilterClick();
                                            }}>
                                            <SVGS.FilterIcon className={classNames({[styles.active]: isMobileFilterActive})} />
                                        </IconButton>
                                ) : (
                                    canEditClients ? <CreateNewButtonV2 onClick={handleCreateClick} /> : null
                                )}
                            </div>
                        </div>
                    </div>
                </PageHeader>
                <PageBody justifyContent={isEmpty ? 'center' : 'flex-start'} alignItems='stretch'>
                    {isEmpty ?
                        <IconTextLarge
                            text={intl.formatMessage({ id: Lx.Client.NO_CLIENTS })}
                            subtext={intl.formatMessage({ id: Lx.Client.CREATE_AND_USE_CLIENT })}
                            icon={<SVGS.ClientsIcon />}
                        />
                        :
                        <ClientFilter
                            search={search}
                            selectedStatus={selectedStatus}
                            isMobile={isMobile}
                            mobileView={mobileView}
                            onFilterChange={handleFilterChange}
                            onMobileClose={handleMobileFilterClose}
                            showDesktopFilter={!isMobile}
                            desktopWrapperClassName={styles.filterRow}>
                            <PageTableContainer>
                                <TiminatorTableV2
                                    columns={tableSettings.columns}
                                    data={tableSettings.data}
                                    desktopMinWidth={925}
                                    hasMore={hasMore}
                                    onLoadMore={handleLoadMore}
                                    onRowClick={canEditClients ? handleRowClick : undefined}
                                    isLoadingMore={isLoadingMore}
                                />
                            </PageTableContainer>
                        </ClientFilter>
                    }
                </PageBody>
                {shouldShowCreateFooterButton &&
                    <PageFooter>
                        <CreateNewButtonV2 onClick={handleCreateClick} />
                    </PageFooter>
                }
            </PageWrapperV2>
            <DeleteDialog
                open={isDeleteDialogOpen}
                onCancel={handleDeleteCancel}
                onConfirmDelete={handleDeleteConfirm}
                deleteHeader={intl.formatMessage({ id: Lx.Client.DELETE_CLIENT })}
                deleteMessage={intl.formatMessage(
                    { id: Lx.Client.DELETE_CLIENT_MESSAGE },
                    { name: selectedClient?.name },
                )}
                title={selectedClient?.name ?? intl.formatMessage({ id: Lx.General.CLIENT })}
            />
        </>
    );
}
